import { AggregateException } from 'src/app/utils/exceptions';
import { DbStore, IndexedDbStore } from './db-store';
import { Callback, CallbackAddMode } from './db-transaction-callback';

export interface DbTransaction {
  addOnCommitCallback(callback: Callback, mode?: CallbackAddMode): void;
  checkHasOnCommitCallback(name: string): boolean;
  getOnCommitCallbackNames(): string[];
  removeOnCommitCallbacks(name: string): void;
  commit(): Promise<void>;
  rollback(): void;
  objectStore(storeName: string): DbStore;
}

export class IndexedDbTransaction implements DbTransaction {
  private callbacksExecuted = false;
  private onCommitCallbacks: Array<Callback> = [];

  constructor(private transaction: IDBTransaction) {
    this.transaction.oncomplete = async args => {
      if (!this.callbacksExecuted) {
        await this.executeOnCommitCallbacks();
      }
    };
  }

  addOnCommitCallback(callback: Callback, mode: CallbackAddMode = CallbackAddMode.RemoveDuplicatesAndPush): void {
    switch (mode) {
      case CallbackAddMode.RemoveDuplicatesAndPush:
        this.removeOnCommitCallbacks(callback.name);
        this.onCommitCallbacks.push(callback);
        break;

      case CallbackAddMode.PushIfNotDuplicate:
        if (this.checkHasOnCommitCallback(callback.name)) {
          this.onCommitCallbacks.push(callback);
        }
        break;

      case CallbackAddMode.PushDuplicate:
        this.onCommitCallbacks.push(callback);
        break;
    }
  }

  checkHasOnCommitCallback(name: string): boolean {
    return this.onCommitCallbacks.some(callback => callback.name === name);
  }

  getOnCommitCallbackNames(): string[] {
    return this.onCommitCallbacks.map(callback => callback.name);
  }

  removeOnCommitCallbacks(name: string): void {
    this.onCommitCallbacks = this.onCommitCallbacks.filter(callback => callback.name !== name);
  }

  private async executeOnCommitCallbacks(): Promise<void> {
    this.callbacksExecuted = true;
    const errors: any[] = [];
    
    for (const callback of this.onCommitCallbacks) {
      try {
        await callback.fn();
      } catch (error) {
        errors.push(error);
      }
    }
    
    if (errors.length > 0) {
      throw new AggregateException(errors);
    }
  }
  
  async commit(): Promise<void> {
    if (!this.callbacksExecuted) {
      await this.executeOnCommitCallbacks();
    }
    // Note: IndexedDB transactions auto-commit, so there's no need to do anything else here
  }

  rollback(): void {
    try {
      this.transaction.abort();
    } catch (error) {
      console.log('Transaction cannot be rolledback', error);
    }
  }

  objectStore(storeName: string): DbStore {
    storeName = storeName.toLowerCase();
    const store = this.transaction.objectStore(storeName);
    return new IndexedDbStore(store);
  }
}
